import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import ManageGraph from './components/table/ManageGraph';
import SummaryGDAGraphs from '@/components/Graphs/SummaryGDA/SummaryGDAGraphs';
import SummaryVDAGraphs from '@/components/Graphs/SummaryVDA/SummaryVDAGraphs';
import MappingResults from '@/components/Mapping/MappingResults';
import { MappingDrillDownProvider } from '@/context/MappingDrillDownContext';
import DrillDownTableContainer from '@/components/Mapping/DrillDownTableContainer';
import DDAGraphs from '@/components/Graphs/DDA/DDAGraphs';
import ScoreModal from './components/modal/ScoreModal';
import { AdjustmentsHorizontalIcon, ChevronDoubleLeftIcon } from '@heroicons/react/16/solid';
import { mapItemsToSearchItems } from '../../../components/Table/types/index';
import { RightAsideSidebar } from './components/table/components/RightAsideSidebar';
import { MappingSidebarColumnProvider } from '@/context/MappingSidebarColumnContext';
import SharedBadge from '@/global/Badge/Badge';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SharedButton from '../../../global/Button/Button';
import './SearchResults.css';

import { useSearchResultsState } from './hooks/useSearchResultsState';
import { LeftPanels } from './components/LeftPanels';
import { FullRecordDetailView } from './components/FullRecordDetailView';
import { BottomDetailPanel } from './components/BottomDetailPanel';
import { TabGridContent } from './components/TabGridContent';
import {
  getSearchItemBadgeLabel,
  getNumericScoreFromSearchItem,
  SearchResultsProps,
} from './utils/searchResultsHelpers';
import { trackEvent } from '@/utils/analytics';

const graphMap: Record<string, any> = {
  GDA: SummaryGDAGraphs,
  VDA: SummaryVDAGraphs,
  DDA: DDAGraphs,
};

function SearchResultsInner({ dispatch }: SearchResultsProps) {
  const {
    // Context
    view,
    source,
    tab,
    searchItems,
    setSearchItems,
    mappingContent,
    restrictChemical,
    isAuthenticated,
    // Auth
    isTrial,
    isAcademic,
    // Sidebar layout
    isSidebarOpen,
    sidebarWidth,
    setSidebarOpen,
    isDesktop,
    // State
    tabIndex,
    selectedView,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    scoreModalOpen,
    setScoreModalOpen,
    refreshScoresDropdown,
    setRefreshScoresDropdown,
    leftInfoPanelItem,
    leftSentencePanel,
    bottomDetailTab,
    setBottomDetailTab,
    bottomDetailRow,
    bottomDetailHeight,
    setBottomDetailHeight,
    sentenceCopied,
    sourceWeights,
    searchItemsDropdownOpen,
    setSearchItemsDropdownOpen,
    graphsColumnModel,
    setGraphsColumnModel,
    summaryColumnModel,
    setSummaryColumnModel,
    summaryColumnOrder,
    setSummaryColumnOrder,
    evidenceColumnModel,
    setEvidenceColumnModel,
    evidenceColumnOrder,
    setEvidenceColumnOrder,
    activeColumnPanel,
    setActiveColumnPanel,
    isMappingUrlHydrated,
    // Refs
    searchItemsDropdownRef,
    sentenceToCopyRef,
    summaryResetRef,
    evidenceResetRef,
    // Computed
    visibleTabs,
    sortedSearchItemsForHeader,
    searchItemsEntityPlural,
    groupedBottomDetails,
    relationSummary,
    columnOrder,
    searchTableSummaryEvidenceToggle,
    isFullRecordMode,
    showSummary,
    showEvidence,
    showManageGraph,
    useMappingSidebarState,
    useDrillDownMapping,
    currentMappingLevel,
    mappingSidebar,
    // Breakdown rows
    scoreBreakdownRow,
    setScoreBreakdownRow,
    prevalenceBreakdownRow,
    setPrevalenceBreakdownRow,
    evidenceTimelineRow,
    setEvidenceTimelineRow,
    // Handlers
    handleTabSelect,
    handleRemoveSearchItem,
    handleClearAllSearchItems,
    handleOpenEntityInfo,
    handleCopySentenceFromPanel,
    handleApplyWeights,
    handleApplySavedScore,
    handleDeleteSavedScore,
    handleClearWeights,
    handleApplySavedColumns,
    handleDeleteSavedColumn,
    handleResetToDefault,
    handleDownloadRowDetail,
    startBottomResize,
    closeBottomDetail,
    closeSentencePanel,
    searchParams,
    setSearchParams,
    defaultFilterValue,
  } = useSearchResultsState(dispatch);

  const hasLeftPanel =
    leftInfoPanelItem ||
    leftSentencePanel ||
    scoreBreakdownRow ||
    prevalenceBreakdownRow ||
    evidenceTimelineRow;

  const sharedGridProps = {
    source,
    view,
    tab,
    searchItems,
    setSearchItems,
    restrictChemical,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    defaultFilterValue,
    dispatch,
    sourceWeights,
    onScoreSaved: () => setRefreshScoresDropdown((prev: number) => prev + 1),
    onOpenSearchItemInfo: handleOpenEntityInfo,
    summaryEvidenceToggle: searchTableSummaryEvidenceToggle,
    summaryColumnModel,
    setSummaryColumnModel,
    summaryColumnOrder,
    setSummaryColumnOrder,
    summaryResetRef,
    evidenceColumnModel,
    setEvidenceColumnModel,
    evidenceColumnOrder,
    setEvidenceColumnOrder,
    evidenceResetRef,
  };

  const sharedSidebarProps = {
    isOpen: isSidebarOpen,
    onClose: () => setSidebarOpen(false),
    source,
    view,
    tab,
    searchItems: mapItemsToSearchItems(searchItems),
    onOpenSearchItemInfo: handleOpenEntityInfo,
    onRemoveSearchItem: handleRemoveSearchItem,
    onClearAllSearchItems: handleClearAllSearchItems,
    showMyScoreSection: !isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated,
    onOpenScoreModal: () => setScoreModalOpen(true),
    onApplySavedColumns: handleApplySavedColumns,
    onDeleteSavedColumns: handleDeleteSavedColumn,
    onApplySavedScore: handleApplySavedScore,
    onDeleteSavedScore: handleDeleteSavedScore,
    onClearWeights: handleClearWeights,
    hasActiveWeights: sourceWeights.length > 0,
    refreshScoresDropdown,
    sourceWeights,
    onResetToDefault: handleResetToDefault,
    isAcademic,
  };

  return (
    <>
      <ScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        onApplyWeights={handleApplyWeights}
        onScoreSaved={() => setRefreshScoresDropdown((prev: number) => prev + 1)}
        refreshTrigger={refreshScoresDropdown}
      />

      {isTrial && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)', paddingTop: '10px' }}>
            Only the top 30 results are shown.{' '}
            <a href="/academic-plans" style={{ color: '#0d1661' }}>
              Upgrade your account{' '}
            </a>
            to see all results.{' '}
            <a href="/plans" style={{ color: '#0d1661' }}>
              View our plans
            </a>
            .
          </p>
        </div>
      )}

      {isFullRecordMode && (
        <FullRecordDetailView
          bottomDetailRow={bottomDetailRow}
          groupedBottomDetails={groupedBottomDetails}
          relationSummary={relationSummary}
          source={source}
          tab={tab}
          isSidebarOpen={isSidebarOpen}
          sidebarWidth={sidebarWidth}
          isDesktop={isDesktop}
          searchParams={searchParams as any}
          setSearchParams={setSearchParams}
          handleDownloadRowDetail={handleDownloadRowDetail}
          bottomDetailTab={bottomDetailTab}
          setBottomDetailTab={setBottomDetailTab}
        />
      )}

      <LeftPanels
        leftInfoPanelItem={leftInfoPanelItem}
        leftSentencePanel={leftSentencePanel}
        scoreBreakdownRow={scoreBreakdownRow}
        prevalenceBreakdownRow={prevalenceBreakdownRow}
        evidenceTimelineRow={evidenceTimelineRow}
        sentenceToCopyRef={sentenceToCopyRef}
        sentenceCopied={sentenceCopied}
        isDesktop={isDesktop}
        view={view}
        onCloseInfoPanel={() => handleOpenEntityInfo(null)}
        onCloseSentencePanel={closeSentencePanel}
        onCloseScoreBreakdown={() => setScoreBreakdownRow(null)}
        onClosePrevalenceBreakdown={() => setPrevalenceBreakdownRow(null)}
        onCloseEvidenceTimeline={() => setEvidenceTimelineRow(null)}
        onCopySentence={handleCopySentenceFromPanel}
      />

      {selectedView === 'graphs' && (
        <RightAsideSidebar
          {...sharedSidebarProps}
          isEvidence={false}
          columnVisibilityModel={graphsColumnModel}
          setColumnVisibilityModel={updater => setGraphsColumnModel(updater)}
          isMapping={false}
        />
      )}

      {selectedView === 'tables' && (
        <RightAsideSidebar
          {...sharedSidebarProps}
          isEvidence={
            useMappingSidebarState
              ? (mappingSidebar.state!.columnPanelToggle?.active ?? 'summary') === 'evidence'
              : showEvidence && activeColumnPanel === 'evidence'
          }
          columnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.columnVisibilityModel
              : activeColumnPanel === 'summary' || !showEvidence
                ? (summaryColumnModel ?? {})
                : (evidenceColumnModel ?? {})
          }
          setColumnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.setColumnVisibilityModel
              : updater => {
                  if (activeColumnPanel === 'summary' || !showEvidence) {
                    setSummaryColumnModel((prev: any) =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  } else {
                    setEvidenceColumnModel((prev: any) =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  }
                }
          }
          columnPanelToggle={
            useMappingSidebarState
              ? mappingSidebar.state!.columnPanelToggle
              : showEvidence
                ? { active: activeColumnPanel, onChange: setActiveColumnPanel }
                : undefined
          }
          isMapping={useMappingSidebarState}
          mappingContent={
            useMappingSidebarState && currentMappingLevel?.mappingContent
              ? (currentMappingLevel.mappingContent as any)
              : mappingContent
          }
          columnOrder={columnOrder}
          setColumnOrder={
            useMappingSidebarState
              ? mappingSidebar.state!.setColumnOrder
              : (order: string[]) => {
                  if (activeColumnPanel === 'summary' || !showEvidence) setSummaryColumnOrder(order);
                  else setEvidenceColumnOrder(order);
                }
          }
        />
      )}

      <div
        className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}${isFullRecordMode ? ' hidden' : ''}`}
        data-testid="search-results"
        style={{
          marginLeft: hasLeftPanel ? (isDesktop ? 340 : 0) : 0,
          marginRight: isSidebarOpen ? (isDesktop ? sidebarWidth : 0) : 0,
          transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out',
          paddingRight: isSidebarOpen ? '1%' : '3%',
          paddingLeft: hasLeftPanel ? '1%' : '3%',
        }}
      >
        <div
          className="pr-1 pl-1 md:p-5 md:pr-5 responsive-search-results"
          style={{ flex: '1', height: '100%', minWidth: 0 }}
        >
          <Tabs
            className="react-tabs superposed-content"
            selectedIndex={tabIndex}
            onSelect={index => handleTabSelect(index)}
          >
            <div className="responsive-search-tabs flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2 lg:flex-nowrap">
              <div className="d-flex min-w-0 flex-1 gap-2 md:justify-start">
                <TabList className="react-tabs__tab-list col-auto">
                  {visibleTabs.map((t: any) => (
                    <Tab key={t.key} className="react-tabs__tab bg-white mb-0 my-2 my-lg-0 m-1">
                      {t.label}
                    </Tab>
                  ))}
                </TabList>
              </div>

              <div className="md:ml-auto flex min-w-0 shrink-0 items-center gap-3">
                {(searchItems?.length ?? 0) > 0 && (
                  <div className="relative flex min-w-0 flex-col items-end sm:max-w-[min(100%,40rem)] lg:max-w-[min(50%,40rem)]">
                    <div ref={searchItemsDropdownRef} className="relative w-full min-w-0 flex items-center">
                      <span className="text-xs font-medium tracking-widest text-uppercase text-gray-500 mr-3 flex items-center">
                        <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                        Search
                      </span>
                      <SharedButton
                        id="search-items-query-dropdown-trigger"
                        aria-expanded={searchItemsDropdownOpen}
                        variant="ghost"
                        size="xs"
                        aria-controls="search-items-query-dropdown-panel"
                        onClick={() => {
                          trackEvent({
                            name: 'dropdown_toggle',
                            buttonId: 'search_items_dropdown_toggle',
                            label: 'Toggle search items dropdown',
                            page: 'search_results',
                            section: 'search_items_header',
                            feature: 'search_items',
                            eventGroup: 'search',
                            eventCategory: 'search',
                          });
                          setSearchItemsDropdownOpen((o: boolean) => !o);
                        }}
                        className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 mb-1 mr-4"
                        iconRight={
                          <ChevronDownIcon
                            className="h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200"
                            style={{ transform: searchItemsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          />
                        }
                      >
                        <span className="min-w-0 flex-1 truncate font-medium text-slate-900">
                          Showing {searchItems.length} {searchItemsEntityPlural}
                        </span>
                      </SharedButton>
                      {searchItemsDropdownOpen && (
                        <div
                          id="search-items-query-dropdown-panel"
                          role="region"
                          aria-labelledby="search-items-query-dropdown-trigger"
                          className="mr-2 absolute right-0 z-[2000] origin-top overflow-hidden rounded-3xl bg-white shadow-xl shadow-slate-900/10"
                          style={{
                            top: 'calc(100% + 0.375rem)',
                            border: '1px dashed rgb(181, 181, 181)',
                            width: 'max-content',
                            maxWidth: '40rem',
                          }}
                        >
                          <div className="max-h-80 md:max-h-28 min-h-0 overflow-y-auto overscroll-contain p-3 sm:max-h-32">
                            <div className="flex flex-wrap gap-2">
                              {sortedSearchItemsForHeader.map((item: any, index: number) => {
                                const { label } = getSearchItemBadgeLabel(item);
                                const score = getNumericScoreFromSearchItem(item);
                                const canOpenInfo = !!handleOpenEntityInfo;
                                return (
                                  <div
                                    key={String(item.id)}
                                    className="max-w-full min-w-0"
                                    onClick={() => {
                                      if (canOpenInfo) handleOpenEntityInfo(item);
                                    }}
                                    onKeyDown={event => {
                                      if (!canOpenInfo) return;
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleOpenEntityInfo(item);
                                      }
                                    }}
                                    role={canOpenInfo ? 'button' : undefined}
                                    tabIndex={canOpenInfo ? 0 : undefined}
                                    aria-label={canOpenInfo ? `Open info for ${label}` : undefined}
                                  >
                                    <SharedBadge
                                      text={
                                        <span className="search-items-tag-content flex min-w-0 max-w-[18rem] items-center gap-1.5">
                                          <span
                                            className="search-items-tag-label min-w-0 flex-1 truncate"
                                            title={label}
                                          >
                                            {label}
                                          </span>
                                          {score != null && (
                                            <span className="shrink-0 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-700">
                                              {score}
                                            </span>
                                          )}
                                          <span aria-hidden="true" className="search-items-tag-divider" />
                                          <button
                                            type="button"
                                            onClick={event => {
                                              event.stopPropagation();
                                              trackEvent({
                                                name: 'remove_search_item',
                                                buttonId: 'search_items_dropdown_remove_item',
                                                label: 'Remove search item from header dropdown',
                                                page: 'search_results',
                                                section: 'search_items_header',
                                                feature: 'search_items',
                                                eventGroup: 'search',
                                                eventCategory: 'search',
                                              });
                                              handleRemoveSearchItem(index);
                                            }}
                                            className="search-items-tag-close shrink-0 border-0 bg-transparent pl-0.5"
                                            aria-label={`Remove ${label}`}
                                          >
                                            <XMarkIcon className="h-3.5 w-3.5" />
                                          </button>
                                        </span>
                                      }
                                      variant="primaryLight"
                                      className="search-items-tag !mb-0 flex h-auto min-h-7 max-w-full justify-start overflow-hidden text-left"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isSidebarOpen && !isDesktop && (
                  <div className="flex shrink-0 items-center mb-2">
                    <BaseTooltip content="Open table controls" position="bottom">
                      <SharedButton
                        type="button"
                        variant="terciary"
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        aria-expanded={isSidebarOpen}
                        aria-label={isSidebarOpen ? 'Hide table controls' : 'Open table controls'}
                        data-testid="grid-controls-sidebar-toggle"
                        size="xs"
                        iconLeft={isDesktop ? <ChevronDoubleLeftIcon className="w-5" /> : null}
                        iconMargin={null}
                        className={`p-0 mb-0 ${!isDesktop ? 'pr-3' : ''}`}
                      >
                        {!isDesktop && (
                          <>
                            <AdjustmentsHorizontalIcon className="w-5 mx-2" />
                            {isSidebarOpen ? 'Hide controls' : 'Controls'}
                          </>
                        )}
                      </SharedButton>
                    </BaseTooltip>
                  </div>
                )}
              </div>
            </div>

            {sourceWeights.length > 0 &&
              selectedView === 'tables' &&
              ['GDA', 'VDA'].includes(tab as string) &&
              !isAcademic &&
              isAuthenticated && (
                <div className="mb-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Custom Score Configuration Active</p>
                      <p className="text-xs text-blue-700 mt-1 mb-0">
                        You have a custom My Score configuration applied to this data.{' '}
                        {isAuthenticated
                          ? 'Use the "My Score" section in the Controls sidebar to manage or clear your configuration.'
                          : 'Use the "My Score" section in the Controls sidebar to modify or clear your configuration.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            <div className="mt-2">
              {visibleTabs.map((t: any) => (
                <TabPanel key={t.key} className="tab-pane">
                  {useDrillDownMapping && selectedView === 'tables' ? (
                    <MappingDrillDownProvider useDrillDown={true}>
                      <DrillDownTableContainer
                        baseLevelId={`base-${t.key}`}
                        baseLabel="Results"
                        dispatch={dispatch}
                        panelTab={t.key}
                        onOpenEntityInfo={handleOpenEntityInfo}
                        isMappingUrlHydrated={isMappingUrlHydrated}
                      >
                        <ScrollElement name="SearchTable">
                          <TabGridContent
                            {...sharedGridProps}
                            panelTab={t.key}
                            selectedView={selectedView}
                            showSummary={showSummary}
                            showEvidence={showEvidence}
                            activeColumnPanel={activeColumnPanel}
                          />
                        </ScrollElement>
                      </DrillDownTableContainer>
                    </MappingDrillDownProvider>
                  ) : (
                    <>
                      <ScrollElement name="SearchTable">
                        <TabGridContent
                          {...sharedGridProps}
                          panelTab={t.key}
                          selectedView={selectedView}
                          showSummary={showSummary}
                          showEvidence={showEvidence}
                          activeColumnPanel={activeColumnPanel}
                        />
                      </ScrollElement>
                      <ScrollElement name="Summary">
                        {selectedView === 'graphs' && showManageGraph && (
                          <ManageGraph
                            source={source}
                            view={view}
                            tab={tab}
                            searchItems={searchItems}
                            setSearchItems={setSearchItems}
                            sectionTitle={t.label}
                            names={searchItems}
                            title={view}
                            Graph={graphMap[t.key]}
                          />
                        )}
                      </ScrollElement>
                      <ScrollElement name="Mapping">
                        {selectedView === 'tables' && mappingContent?.isActive && (
                          <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                        )}
                      </ScrollElement>
                    </>
                  )}
                </TabPanel>
              ))}
            </div>
          </Tabs>
        </div>
      </div>

      {bottomDetailRow && !isFullRecordMode && (
        <BottomDetailPanel
          bottomDetailRow={bottomDetailRow}
          bottomDetailHeight={bottomDetailHeight}
          groupedBottomDetails={groupedBottomDetails}
          relationSummary={relationSummary}
          source={source}
          tab={tab}
          isSidebarOpen={isSidebarOpen}
          sidebarWidth={sidebarWidth}
          isDesktop={isDesktop}
          searchParams={searchParams as any}
          setSearchParams={setSearchParams}
          onClose={closeBottomDetail}
          startBottomResize={startBottomResize}
          bottomDetailTab={bottomDetailTab}
          setBottomDetailTab={setBottomDetailTab}
          setBottomDetailHeight={setBottomDetailHeight}
        />
      )}
    </>
  );
}

function SearchResults(props: SearchResultsProps) {
  return (
    <MappingSidebarColumnProvider>
      <SearchResultsInner {...props} />
    </MappingSidebarColumnProvider>
  );
}

export default SearchResults;
